
import { ChatMessage, MessageSegment, messageSegments } from 'lib/twitchChatParse'
import logger from 'logger'

const log = logger.extend('third-party-emotes')

const FETCH_TIMEOUT_MS = 5000
const CHANNEL_REFRESH_BASE_MS = 10 * 60 * 1000
const CHANNEL_REFRESH_JITTER_MS = 2 * 60 * 1000
const GLOBAL_REFRESH_BASE_MS = 60 * 60 * 1000
const GLOBAL_REFRESH_JITTER_MS = 5 * 60 * 1000
const MANUAL_REFRESH_MIN_INTERVAL_MS = 60 * 1000

export type EmoteProvider = '7TV' | 'BTTV' | 'FFZ'
export type EmoteScope = 'global' | 'channel'

export interface EmoteEntry {
  name: string
  provider: EmoteProvider
  scope: EmoteScope
  urls: { x1: string, x2: string, x4?: string }
  animated: boolean
  zeroWidth: boolean
  width?: number
  height?: number
}

export type EmoteLookup = (name: string, roomId: string) => EmoteEntry | null

const randomFloat = (): number => {
  const buf = new Uint32Array(1)
  crypto.getRandomValues(buf)
  return buf[0] / 2 ** 32
}

const logFetchFailure = (provider: EmoteProvider, scope: string, err: unknown): void => {
  log({ provider, scope, refresh: 'failed', err: err instanceof Error ? err.name : 'unknown' })
}

export const EMOTE_ABSENT_TTL_MS = 6 * 60 * 60 * 1000
type AbsentCacheProvider = '7tv' | 'bttv' | 'ffz'
const ABSENT_KEY_PREFIX = 'stream-tools.emote-absent.v1.'
const CACHEABLE_BROADCASTER_ID = /^[0-9]{1,20}$/
const STORED_EXPIRY = /^[0-9]{1,16}$/

const absentMemory = new Map<string, number>()

const absentKey = (provider: AbsentCacheProvider, broadcasterUserId: string): string =>
  `${ABSENT_KEY_PREFIX}${provider}.${broadcasterUserId}`

const storageGet = (key: string): string | null => {
  try {
    return globalThis.localStorage.getItem(key)
  } catch {
    return null
  }
}
const storageSet = (key: string, value: string): void => {
  try {
    globalThis.localStorage.setItem(key, value)
  } catch {
  }
}
const storageRemove = (key: string): void => {
  try {
    globalThis.localStorage.removeItem(key)
  } catch {
  }
}

const isMarkedAbsent = (provider: AbsentCacheProvider, broadcasterUserId: string, now: number): boolean => {
  if (!CACHEABLE_BROADCASTER_ID.test(broadcasterUserId)) return false
  const key = absentKey(provider, broadcasterUserId)
  let expiresAt = absentMemory.get(key)
  if (expiresAt === undefined) {
    const raw = storageGet(key)
    if (raw === null) return false
    const parsed = STORED_EXPIRY.test(raw) ? Number(raw) : Number.NaN
    if (!Number.isSafeInteger(parsed) || parsed > now + EMOTE_ABSENT_TTL_MS) {
      storageRemove(key)
      return false
    }
    expiresAt = parsed
    absentMemory.set(key, expiresAt)
  }
  if (expiresAt <= now) {
    absentMemory.delete(key)
    storageRemove(key)
    return false
  }
  return true
}

const markAbsent = (provider: AbsentCacheProvider, broadcasterUserId: string, now: number): void => {
  if (!CACHEABLE_BROADCASTER_ID.test(broadcasterUserId)) return
  const key = absentKey(provider, broadcasterUserId)
  const expiresAt = now + EMOTE_ABSENT_TTL_MS
  absentMemory.set(key, expiresAt)
  storageSet(key, String(expiresAt))
}

const clearAbsent = (provider: AbsentCacheProvider, broadcasterUserId: string): void => {
  if (!CACHEABLE_BROADCASTER_ID.test(broadcasterUserId)) return
  const key = absentKey(provider, broadcasterUserId)
  absentMemory.delete(key)
  storageRemove(key)
}

const CHANNEL_ABSENT = Symbol('channel-absent')
type ChannelResult = EmoteEntry[] | typeof CHANNEL_ABSENT

const fetchJson = async (url: string, emptyOn404: boolean): Promise<unknown> => {
  const res = await fetch(url, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
  if (emptyOn404 && res.status === 404) return CHANNEL_ABSENT
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return await res.json()
}

interface SevenTvFile { name: string, width?: number, height?: number }
interface SevenTvHost { url?: string, files?: SevenTvFile[] }
interface SevenTvEmoteData { animated?: boolean, flags?: number, host?: SevenTvHost }
interface SevenTvEmote { name?: string, flags?: number, data?: SevenTvEmoteData }

const SEVEN_TV_SET_FLAG_ZERO_WIDTH = 1
const SEVEN_TV_DATA_FLAG_ZERO_WIDTH = 256

const sevenTvEntry = (e: SevenTvEmote, scope: EmoteScope): EmoteEntry[] => {
  const hostUrl = e.data?.host?.url
  if (typeof e.name !== 'string' || e.name === '' ||
    typeof hostUrl !== 'string' || hostUrl === '') return []
  const base = hostUrl.startsWith('//') ? `https:${hostUrl}` : hostUrl
  const files = e.data?.host?.files ?? []
  const f2 = files.find((f) => f.name === '2x.webp')
  return [{
    name: e.name,
    provider: '7TV',
    scope,
    urls: { x1: `${base}/1x.webp`, x2: `${base}/2x.webp`, x4: `${base}/4x.webp` },
    animated: e.data?.animated === true,
    zeroWidth: ((e.flags ?? 0) & SEVEN_TV_SET_FLAG_ZERO_WIDTH) !== 0 ||
      ((e.data?.flags ?? 0) & SEVEN_TV_DATA_FLAG_ZERO_WIDTH) !== 0,
    width: f2?.width,
    height: f2?.height
  }]
}

const sevenTvGlobal = async (): Promise<EmoteEntry[]> => {
  const data = await fetchJson('https://7tv.io/v3/emote-sets/global', false) as
    { emotes?: SevenTvEmote[] } | null
  return (data?.emotes ?? []).flatMap((e) => sevenTvEntry(e, 'global'))
}

const sevenTvChannel = async (broadcasterUserId: string): Promise<ChannelResult> => {
  const raw = await fetchJson(`https://7tv.io/v3/users/twitch/${broadcasterUserId}`, true)
  if (raw === CHANNEL_ABSENT) return CHANNEL_ABSENT
  const data = raw as { emote_set?: { emotes?: SevenTvEmote[] } } | null
  return (data?.emote_set?.emotes ?? []).flatMap((e) => sevenTvEntry(e, 'channel'))
}

interface BttvEmote { id?: string, code?: string, imageType?: string, animated?: boolean }

const BTTV_ZERO_WIDTH_NAMES = new Set([
  'SoSnowy', 'IceCold', 'TopHat', 'CandyCane', 'ReinDeer', 'cvHazmat', 'cvMask'
])

const bttvEntry = (e: BttvEmote, scope: EmoteScope): EmoteEntry[] => {
  if (typeof e.id !== 'string' || e.id === '' ||
    typeof e.code !== 'string' || e.code === '') return []
  const base = `https://cdn.betterttv.net/emote/${e.id}`
  return [{
    name: e.code,
    provider: 'BTTV',
    scope,
    urls: { x1: `${base}/1x`, x2: `${base}/2x`, x4: `${base}/3x` },
    animated: e.animated ?? e.imageType === 'gif',
    zeroWidth: BTTV_ZERO_WIDTH_NAMES.has(e.code)
  }]
}

const bttvGlobal = async (): Promise<EmoteEntry[]> => {
  const data = await fetchJson('https://api.betterttv.net/3/cached/emotes/global', false) as
    BttvEmote[] | null
  return (data ?? []).flatMap((e) => bttvEntry(e, 'global'))
}

const bttvChannel = async (broadcasterUserId: string): Promise<ChannelResult> => {
  const raw = await fetchJson(`https://api.betterttv.net/3/cached/users/twitch/${broadcasterUserId}`, true)
  if (raw === CHANNEL_ABSENT) return CHANNEL_ABSENT
  const data = raw as { channelEmotes?: BttvEmote[], sharedEmotes?: BttvEmote[] } | null
  return [...(data?.channelEmotes ?? []), ...(data?.sharedEmotes ?? [])]
    .flatMap((e) => bttvEntry(e, 'channel'))
}

interface FfzEmote {
  name?: string
  width?: number
  height?: number
  urls?: Record<string, string>
  animated?: Record<string, string> | null
}
interface FfzSet { emoticons?: FfzEmote[] }

const withScheme = (url: string): string => url.startsWith('//') ? `https:${url}` : url

const ffzUrl = (e: FfzEmote, scale: '1' | '2' | '4'): string | null => {
  const animatedUrl = e.animated?.[scale]
  if (typeof animatedUrl === 'string' && animatedUrl !== '') return `${withScheme(animatedUrl)}.webp`
  const url = e.urls?.[scale]
  if (typeof url === 'string' && url !== '') return withScheme(url)
  return null
}

const ffzEntry = (e: FfzEmote, scope: EmoteScope): EmoteEntry[] => {
  if (typeof e.name !== 'string' || e.name === '') return []
  const x1 = ffzUrl(e, '1')
  if (x1 === null) return []
  const x2 = ffzUrl(e, '2') ?? x1
  return [{
    name: e.name,
    provider: 'FFZ',
    scope,
    urls: { x1, x2, x4: ffzUrl(e, '4') ?? x2 },
    animated: e.animated != null,
    zeroWidth: false,
    width: e.width,
    height: e.height
  }]
}

const ffzSetsEmotes = (
  sets: Record<string, FfzSet> | undefined,
  setIds: string[] | null,
  scope: EmoteScope
): EmoteEntry[] => {
  if (sets == null) return []
  const ids = setIds ?? Object.keys(sets)
  return ids.flatMap((id) => (sets[id]?.emoticons ?? []).flatMap((e) => ffzEntry(e, scope)))
}

const ffzGlobal = async (): Promise<EmoteEntry[]> => {
  const data = await fetchJson('https://api.frankerfacez.com/v1/set/global', false) as
    { default_sets?: number[], sets?: Record<string, FfzSet> } | null
  const ids = (data?.default_sets ?? []).map((n) => String(n))
  return ffzSetsEmotes(data?.sets, ids.length > 0 ? ids : null, 'global')
}

const ffzChannel = async (broadcasterUserId: string): Promise<ChannelResult> => {
  const raw = await fetchJson(`https://api.frankerfacez.com/v1/room/id/${broadcasterUserId}`, true)
  if (raw === CHANNEL_ABSENT) return CHANNEL_ABSENT
  const data = raw as { sets?: Record<string, FfzSet> } | null
  return ffzSetsEmotes(data?.sets, null, 'channel')
}

type ProviderKey = 'sevenTv' | 'bttv' | 'ffz'

interface ProviderDef {
  key: ProviderKey
  name: EmoteProvider
  cacheKey: AbsentCacheProvider
  global: () => Promise<EmoteEntry[]>
  channel: (broadcasterUserId: string) => Promise<ChannelResult>
}

const PROVIDERS: ProviderDef[] = [
  { key: 'ffz', name: 'FFZ', cacheKey: 'ffz', global: ffzGlobal, channel: ffzChannel },
  { key: 'bttv', name: 'BTTV', cacheKey: 'bttv', global: bttvGlobal, channel: bttvChannel },
  { key: 'sevenTv', name: '7TV', cacheKey: '7tv', global: sevenTvGlobal, channel: sevenTvChannel }
]

const foldEntries = (entries: EmoteEntry[]): Map<string, EmoteEntry> =>
  entries.reduce((map, e) => map.set(e.name, e), new Map<string, EmoteEntry>())

const foldSettled = (
  results: Array<PromiseSettledResult<EmoteEntry[]>>,
  scope: string
): Map<string, EmoteEntry> =>
  results.reduce((map, result, i) => {
    if (result.status === 'rejected') {
      logFetchFailure(PROVIDERS[i].name, scope, result.reason)
      return map
    }
    return result.value.reduce((m, e) => m.set(e.name, e), map)
  }, new Map<string, EmoteEntry>())

export const loadGlobalEmotes = async (): Promise<Map<string, EmoteEntry>> => {
  const results = await Promise.allSettled(PROVIDERS.map(async (p) => await p.global()))
  return foldSettled(results, 'global')
}

export const loadChannelEmotes = async (broadcasterUserId: string): Promise<Map<string, EmoteEntry>> => {
  const results = await Promise.allSettled(PROVIDERS.map(async (p) => {
    const result = await p.channel(broadcasterUserId)
    return result === CHANNEL_ABSENT ? [] : result
  }))
  return foldSettled(results, 'channel')
}

export interface EmoteProviderToggles {
  sevenTv?: boolean
  bttv?: boolean
  ffz?: boolean
}

export interface EmoteStoreOptions {
  providers?: EmoteProviderToggles
  freshCheck?: boolean
  onCatalog?: (catalog: EmoteCatalog) => void
}

export interface EmoteCatalog {
  global: readonly EmoteEntry[]
  channel: readonly EmoteEntry[]
}

export interface EmoteStore {
  refreshNow: () => void
  dispose: () => void
}

export const createEmoteStore = (
  broadcasterUserIds: string[],
  onSwap: (lookup: EmoteLookup) => void,
  options: EmoteStoreOptions = {}
): EmoteStore => {
  const toggles = options.providers ?? {}
  const enabled: Record<ProviderKey, boolean> = {
    sevenTv: toggles.sevenTv !== false,
    bttv: toggles.bttv !== false,
    ffz: toggles.ffz !== false
  }
  const active = PROVIDERS.filter((p) => enabled[p.key])

  const maps: Record<ProviderKey, {
    global: Map<string, EmoteEntry>
    channels: Map<string, Map<string, EmoteEntry>>
  }> = {
    sevenTv: { global: new Map(), channels: new Map() },
    bttv: { global: new Map(), channels: new Map() },
    ffz: { global: new Map(), channels: new Map() }
  }

  let disposed = false
  let globalTimer: ReturnType<typeof globalThis.setTimeout> | undefined
  let channelTimer: ReturnType<typeof globalThis.setTimeout> | undefined
  let lastManualRefreshAt = Date.now()

  const rebuildAndSwap = (): void => {
    if (disposed) return
    const foldedGlobal = new Map<string, EmoteEntry>()
    PROVIDERS.forEach((p) => {
      maps[p.key].global.forEach((e, name) => foldedGlobal.set(name, e))
    })
    const foldedChannels = new Map<string, Map<string, EmoteEntry>>()
    broadcasterUserIds.forEach((id) => {
      const folded = new Map<string, EmoteEntry>()
      PROVIDERS.forEach((p) => {
        maps[p.key].channels.get(id)?.forEach((e, name) => folded.set(name, e))
      })
      foldedChannels.set(id, folded)
    })
    onSwap((name, roomId) =>
      foldedChannels.get(roomId)?.get(name) ?? foldedGlobal.get(name) ?? null)
    const onCatalog = options.onCatalog
    if (onCatalog != null) {
      onCatalog({
        global: Array.from(foldedGlobal.values()),
        channel: Array.from(foldedChannels.values()).flatMap((m) => Array.from(m.values()))
      })
    }
  }

  const refreshScope = async (scope: EmoteScope, ignoreAbsentMarks = false): Promise<void> => {
    const now = Date.now()
    const jobs = active.flatMap((p): Array<Promise<void>> => {
      if (scope === 'global') {
        return [
          p.global()
            .then((entries) => { maps[p.key].global = foldEntries(entries) })
            .catch((err: unknown) => { logFetchFailure(p.name, 'global', err) })
        ]
      }
      return broadcasterUserIds.flatMap((id): Array<Promise<void>> => {
        if (!ignoreAbsentMarks && isMarkedAbsent(p.cacheKey, id, now)) return []
        return [
          p.channel(id)
            .then((result) => {
              if (result === CHANNEL_ABSENT) {
                markAbsent(p.cacheKey, id, Date.now())
                maps[p.key].channels.set(id, new Map())
                return
              }
              clearAbsent(p.cacheKey, id)
              maps[p.key].channels.set(id, foldEntries(result))
            })
            .catch((err: unknown) => { logFetchFailure(p.name, 'channel', err) })
        ]
      })
    })
    await Promise.all(jobs)
    rebuildAndSwap()
  }

  const jitteredMs = (base: number, jitter: number): number =>
    base + Math.round((randomFloat() * 2 - 1) * jitter)

  const scheduleGlobalRefresh = (): void => {
    if (disposed) return
    globalTimer = globalThis.setTimeout(() => {
      void refreshScope('global').finally(scheduleGlobalRefresh)
    }, jitteredMs(GLOBAL_REFRESH_BASE_MS, GLOBAL_REFRESH_JITTER_MS))
  }

  const scheduleChannelRefresh = (): void => {
    if (disposed) return
    channelTimer = globalThis.setTimeout(() => {
      void refreshScope('channel').finally(scheduleChannelRefresh)
    }, jitteredMs(CHANNEL_REFRESH_BASE_MS, CHANNEL_REFRESH_JITTER_MS))
  }

  void refreshScope('global').finally(scheduleGlobalRefresh)
  if (broadcasterUserIds.length > 0) {
    void refreshScope('channel', options.freshCheck === true).finally(scheduleChannelRefresh)
  }

  return {
    refreshNow: () => {
      if (disposed) return
      const now = Date.now()
      if (now - lastManualRefreshAt < MANUAL_REFRESH_MIN_INTERVAL_MS) return
      lastManualRefreshAt = now
      void refreshScope('channel')
      void refreshScope('global')
    },
    dispose: () => {
      disposed = true
      globalThis.clearTimeout(globalTimer)
      globalThis.clearTimeout(channelTimer)
    }
  }
}

export interface ThirdPartyEmoteSegment {
  kind: 'emote3p'
  entry: EmoteEntry
  alt: string
}

export type OverlaySegment = MessageSegment | ThirdPartyEmoteSegment

export const applyThirdPartyEmotes = (
  segments: MessageSegment[],
  lookup: (name: string) => EmoteEntry | null
): OverlaySegment[] =>
  segments.flatMap((segment): OverlaySegment[] => {
    if (segment.kind !== 'text') return [segment]
    return segment.text.split(/(\s+)/).reduce<OverlaySegment[]>((acc, part) => {
      if (part === '') return acc
      const entry = /\s/.test(part) ? null : lookup(part)
      if (entry !== null) return [...acc, { kind: 'emote3p', entry, alt: part }]
      const last = acc[acc.length - 1]
      if (last !== undefined && last.kind === 'text') {
        return [...acc.slice(0, -1), { kind: 'text', text: last.text + part }]
      }
      return [...acc, { kind: 'text', text: part }]
    }, [])
  })

export const overlayLineSegments = (
  message: Pick<ChatMessage, 'platform' | 'text' | 'emotes' | 'roomId'>,
  lookup: EmoteLookup
): OverlaySegment[] => {
  const segments = messageSegments(message.text, message.emotes)
  if (message.platform === 'YOUTUBE') return segments
  return applyThirdPartyEmotes(segments, (name) => lookup(name, message.roomId))
}

export interface WallEmote {
  url: string
  alt: string
  animated: boolean
  aspect: number
}

export const nativeEmoteUrlLarge = (id: string): string =>
  `https://static-cdn.jtvnw.net/emoticons/v2/${id}/default/dark/3.0`

export const extractEmotes = (
  message: ChatMessage,
  lookup: EmoteLookup | null,
  { includeNative = true }: { includeNative?: boolean } = {}
): WallEmote[] =>
  messageSegments(message.text, includeNative ? message.emotes : [])
    .flatMap((segment): WallEmote[] => {
      if (segment.kind === 'emote') {
        return [{ url: nativeEmoteUrlLarge(segment.id), alt: segment.alt, animated: false, aspect: 1 }]
      }
      if (lookup === null) return []
      return segment.text.split(/\s+/).flatMap((token): WallEmote[] => {
        if (token === '') return []
        const entry = lookup(token, message.roomId)
        if (entry === null || entry.zeroWidth) return []
        const aspect = entry.width != null && entry.height != null && entry.height > 0
          ? entry.width / entry.height
          : 1
        return [{ url: entry.urls.x4 ?? entry.urls.x2, alt: entry.name, animated: entry.animated, aspect }]
      })
    })
